import { getLocationLayers, patchLocationLayerTitle, patchLocationLayerIsActive, patchLocationLayerLocationRestrictions, patchLocationLayerActiveVersionId, postLocationLayer, postLocationLayerVersion, deleteLocationLayer, deleteLocationLayerVersion, reorderLocationLayers } from '@client-shared/api/location-layers.api'
import { sortLocationLayersByOrder } from '@client-shared/utils/sort-functions'

import constants from '@/config/constants'

export default {
  namespaced: true,

  state: () => {
    return {
      list: [],
      // Project id this store's `list` was last fetched for. Lets the socket
      // reconnect catch-up skip stores that have never been loaded for the
      // current project (see cliesrc/utils/refetch-project-data.js).
      loadedProjectId: null,
    }
  },

  getters: {
    getListMap: (state) => {
      return new Map(state.list.map(locationLayer => [
        locationLayer._id,
        locationLayer,
      ]))
    },

    getById: (state, getters) => {
      return locationLayerId => getters.getListMap.get(locationLayerId)
    },

    listSelectable: (state) => {
      return state.list.filter(locationLayer => locationLayer.computed.activeVersion?.tileProcessing.status !== constants.TILE_PROCESSING_STATUS.ERROR && locationLayer.isActive)
    },

    isLocationLayerProcessing () {
      return locationLayer => {
        if (!locationLayer) {
          return false
        }

        const tileProcessing = locationLayer.computed.activeVersion?.tileProcessing

        if (!tileProcessing) {
          return false
        }

        return tileProcessing.status === constants.TILE_PROCESSING_STATUS.NOT_STARTED ||
        tileProcessing.status === constants.TILE_PROCESSING_STATUS.PROCESSING ||
        tileProcessing.status === constants.TILE_PROCESSING_STATUS.UPLOADING
      }
    },
  },

  mutations: {
    SET_LIST (state, list) {
      state.list = [ ...list ]
        .sort(sortLocationLayersByOrder)
    },

    SET_LOADED_PROJECT_ID (state, projectId) {
      state.loadedProjectId = projectId
    },

    // REPLACE_LOCATION_LAYER and ADD_LOCATION_LAYER replace the list instead of changing it in place:
    // the location layer sorts key their lookup by the list (client-shared/utils/sort-functions.js)
    REPLACE_LOCATION_LAYER (state, updatedLocationLayer) {
      const existingLocationLayer = state.list.findIndex(locationLayer => locationLayer._id === updatedLocationLayer._id)

      if (existingLocationLayer !== -1) {
        const isUpdateNewer = new Date(updatedLocationLayer.modified) > new Date(state.list[existingLocationLayer].modified)

        if (isUpdateNewer) {
          state.list = state.list.map((locationLayer, index) => index === existingLocationLayer ? updatedLocationLayer : locationLayer)
        }
      }
    },

    ADD_LOCATION_LAYER (state, newLocationLayer) {
      const isInList = state.list.some(locationLayer => locationLayer._id === newLocationLayer._id)

      if (isInList) {
        return
      }

      state.list = [
        ...state.list,
        newLocationLayer,
      ]
    },

    REMOVE_LOCATION_LAYER (state, locationLayerId) {
      state.list = state.list.filter(locationLayer => locationLayer._id !== locationLayerId)
    },

    FULL_RESET (state) {
      state.list = []
      state.loadedProjectId = null
    },
  },

  actions: {
    async fetch ({ commit, rootState }, { projectId }) {
      const locationLayers = await getLocationLayers({
        axios: this.$axios,
        projectId,
      })

      // The project was switched while the request was in flight: drop the previous project's list
      if (rootState.project.requestedProjectId !== projectId) {
        return
      }

      commit('SET_LIST', locationLayers)
      commit('SET_LOADED_PROJECT_ID', projectId)
    },

    async updateTitle ({ commit }, { locationLayer, title }) {
      const updatedLocationLayer = await patchLocationLayerTitle({
        axios: this.$axios,
        locationLayer,
        title,
      })

      commit('REPLACE_LOCATION_LAYER', updatedLocationLayer)
    },

    async updateIsActive ({ commit }, { locationLayer, isActive }) {
      const updatedLocationLayer = await patchLocationLayerIsActive({
        axios: this.$axios,
        locationLayer,
        isActive,
      })

      commit('REPLACE_LOCATION_LAYER', updatedLocationLayer)
    },

    async updateLocationRestrictions ({ commit }, { locationLayer, locationRestrictions }) {
      const updatedLocationLayer = await patchLocationLayerLocationRestrictions({
        axios: this.$axios,
        locationLayer,
        locationRestrictions,
      })

      commit('REPLACE_LOCATION_LAYER', updatedLocationLayer)
    },

    async create ({ commit }, { locationLayer }) {
      const newLocationLayer = await postLocationLayer({
        axios: this.$axios,
        locationLayer,
      })

      commit('ADD_LOCATION_LAYER', newLocationLayer)

      return newLocationLayer
    },

    async delete ({ commit }, locationLayerId) {
      await deleteLocationLayer({
        axios: this.$axios,
        locationLayerId,
      })

      commit('REMOVE_LOCATION_LAYER', locationLayerId)
    },

    async createVersion ({ commit }, { locationLayer, sourcePfFileId }) {
      const updatedLocationLayer = await postLocationLayerVersion({
        axios: this.$axios,
        locationLayerId: locationLayer._id,
        sourcePfFileId,
      })

      commit('REPLACE_LOCATION_LAYER', updatedLocationLayer)

      return updatedLocationLayer
    },

    async activateVersion ({ commit }, { locationLayer, activeVersionId }) {
      const updatedLocationLayer = await patchLocationLayerActiveVersionId({
        axios: this.$axios,
        locationLayer,
        activeVersionId,
      })

      commit('REPLACE_LOCATION_LAYER', updatedLocationLayer)

      return updatedLocationLayer
    },

    async discardVersion ({ dispatch }, { locationLayerId, locationLayerVersionId, projectId }) {
      await deleteLocationLayerVersion({
        axios: this.$axios,
        locationLayerId,
        locationLayerVersionId,
      })

      await dispatch('fetch', { projectId })
    },

    async reorder  ({ commit, rootState }, { projectId, oldValue, newValue }) {
      const updatedLocationLayers = await reorderLocationLayers({
        axios: this.$axios,
        projectId,
        oldValue,
        newValue,
      })

      commit('SET_LIST', updatedLocationLayers)
    },
  },
}
